import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import api from '../services/api';
import { useAuth } from '../auth/AuthContext';
import Loading from '../components/Loading';
import { getApiUrl } from '../config/runtimeConfig';

import { clearAuthStorage } from '../services/api';
const SOCKET_URL = getApiUrl().replace(/\/api$/, '');

export default function Dashboard() {
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState('');
  const socketRef = useRef(null);
  const navigate = useNavigate();
  const { user: authenticatedUser, initialized: authInitialized, logout: endSession } = useAuth();

  useEffect(() => {
    if (!authInitialized || !authenticatedUser) return;

    api.auth.getProfile()
      .then(res => {
        // Backend returns { success, data: { user, wallet } }
        const data = res.data.data;
        setProfile(data);
      })
      .catch(() => {
        clearAuthStorage();
        window.location.href = '/login';
      });

    // Set up Socket.IO listener for stake match notifications
    socketRef.current = io(SOCKET_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
    });

    socketRef.current.on('stake:matched', (data) => {
      console.log('Match found!', data);
      if (data.gameType === 'whot' && data.gameId) {
        navigate('/play/whot/' + data.gameId);
      } else if (data.gameType === 'whot' && data.gameId) {
        navigate(`/play/whot/${data.gameId}`);
      }
    });

    return () => {
      if (socketRef.current) socketRef.current.disconnect();
    };
  }, [navigate, authInitialized, authenticatedUser]);

  const logout = async () => {
    await endSession();
    navigate('/', { replace: true });
  };

  if (!profile) return <Loading variant="page" label="Loading your dashboard…" />;

  const { user, wallet } = profile;
  const publicId = user?.publicId || user?.public_id;

  return (
    <div style={{ paddingTop: '20px' }}>
      {publicId && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          padding: '14px 16px',
          marginBottom: '18px',
          borderRadius: '12px',
          background: '#fff7ed',
          border: '1px solid #fed7aa',
        }}>
          <div>
            <div style={{ fontSize: '11px', color: '#9a3412', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>9jaWin ID</div>
            <div style={{ fontSize: '20px', fontWeight: 800, letterSpacing: '0.08em' }}>{publicId}</div>
          </div>
          <button
            type="button"
            onClick={() => navigator.clipboard?.writeText(publicId)}
            style={{ background: '#fff', border: '1px solid #fdba74', borderRadius: '8px', padding: '8px 12px', cursor: 'pointer', fontWeight: 700 }}
          >Copy ID</button>
        </div>
      )}

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px',
      }}>
        <h1 style={{ fontSize: '1.5rem' }}>9jaWin</h1>
        <button
          onClick={logout}
          style={{
            background: 'transparent',
            border: '1px solid #ff4444',
            color: '#ff4444',
            padding: '8px 16px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '600',
            transition: 'background 0.2s',
          }}
        >
          Logout
        </button>
      </div>

      {error && (
        <div style={{
          background: 'rgba(255,77,77,0.1)',
          border: '1px solid #ff4444',
          color: '#ff4444',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '16px',
        }}>
          {error}
        </div>
      )}

      {/* User Card */}
      <div style={{
        background: '#111111',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '20px',
        border: '1px solid #2a2a2a',
      }}>
        <p style={{ color: '#888', fontSize: '13px', marginBottom: '4px' }}>
          Welcome back,
        </p>
        <h2 style={{ fontSize: '1.25rem', marginBottom: '4px' }}>
          {user?.username || user?.email}
        </h2>
        {user?.email && (
          <p style={{ color: '#666', fontSize: '13px', marginBottom: '12px' }}>
            {user.email}
          </p>
        )}
        <p style={{
          color: '#00d4aa',
          fontSize: '1.75rem',
          fontWeight: 'bold',
          margin: 0,
        }}>
          ₦{wallet?.balance != null ? Number(wallet.balance).toLocaleString() : '0.00'}
        </p>
      </div>

      {/* Navigation Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '12px',
      }}>
        <Link
          to="/profile"
          style={{
            background: '#fff',
            border: '1px solid #eee',
            borderRadius: '12px',
            padding: '20px',
            textDecoration: 'none',
            color: '#111',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '2rem', marginBottom: '8px' }}>👤</div>
          <div style={{ fontWeight: '600', fontSize: '14px' }}>Profile</div>
          <div style={{ fontSize: '11px', color: '#f97316', marginTop: 5 }}>{publicId}</div>
        </Link>

        <Link
          to="/wallet"
          style={{
            background: '#111111',
            border: '1px solid #2a2a2a',
            padding: '20px',
            borderRadius: '12px',
            textAlign: 'center',
            color: '#fff',
            textDecoration: 'none',
            transition: 'border-color 0.2s',
          }}
        >
          <div style={{ fontSize: '2rem', marginBottom: '8px' }}>💰</div>
          <div style={{ fontWeight: '600', fontSize: '14px' }}>Wallet</div>
        </Link>

        <Link
          to="/tournaments"
          style={{
            background: '#111111',
            border: '1px solid #2a2a2a',
            padding: '20px',
            borderRadius: '12px',
            textAlign: 'center',
            color: '#fff',
            textDecoration: 'none',
            transition: 'border-color 0.2s',
          }}
        >
          <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🏆</div>
          <div style={{ fontWeight: '600', fontSize: '14px' }}>Tournaments</div>
        </Link>

        <Link
          to="/leaderboard"
          style={{
            background: '#111111',
            border: '1px solid #2a2a2a',
            padding: '20px',
            borderRadius: '12px',
            textAlign: 'center',
            color: '#fff',
            textDecoration: 'none',
            transition: 'border-color 0.2s',
          }}
        >
          <div style={{ fontSize: '2rem', marginBottom: '8px' }}>📊</div>
          <div style={{ fontWeight: '600', fontSize: '14px' }}>Leaderboard</div>
        </Link>

        <Link
          to="/achievements"
          style={{
            background: '#1a1608', border: '1px solid #6b4b0a', borderRadius: '12px', padding: '20px',
            textDecoration: 'none', color: '#fff', textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🏆</div>
          <div style={{ fontWeight: '600', fontSize: '14px', color: '#f59e0b' }}>Achievements</div>
          <div style={{ fontSize: '11px', color: '#888', marginTop: 5 }}>Permanent milestones</div>
        </Link>

        <Link
          to="/notifications"
          style={{
            background: '#fff',
            border: '1px solid #eee',
            padding: '20px',
            borderRadius: '12px',
            textAlign: 'center',
            color: '#111',
            textDecoration: 'none',
          }}
        >
          <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🔔</div>
          <div style={{ fontWeight: '600', fontSize: '14px' }}>Notifications</div>
        </Link>

        <div
          onClick={() => navigate('/')}
          style={{
            background: '#111111',
            border: '1px solid #2a2a2a',
            padding: '20px',
            borderRadius: '12px',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'border-color 0.2s',
          }}
        >
          <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🎮</div>
          <div style={{ fontWeight: '600', fontSize: '14px' }}>Play Now</div>
        </div>

        <div
          onClick={() => navigate('/friends')}
          style={{
            background: '#111111',
            border: '1px solid #2a2a2a',
            padding: '20px',
            borderRadius: '12px',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'border-color 0.2s',
          }}
        >
          <div style={{ fontSize: '2rem', marginBottom: '8px' }}>👥</div>
          <div style={{ fontWeight: '600', fontSize: '14px' }}>Friends</div>
        </div>

        <div
          onClick={() => navigate('/rooms')}
          style={{
            background: '#111111',
            border: '1px solid #2a2a2a',
            padding: '20px',
            borderRadius: '12px',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'border-color 0.2s',
          }}
        >
          <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🚪</div>
          <div style={{ fontWeight: '600', fontSize: '14px' }}>Friend Rooms</div>
        </div>

        <div
          onClick={() => navigate('/referrals')}
          style={{
            background: '#111111',
            border: '1px solid #2a2a2a',
            padding: '20px',
            borderRadius: '12px',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'border-color 0.2s',
          }}
        >
          <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🎁</div>
          <div style={{ fontWeight: '600', fontSize: '14px' }}>Invite Friends</div>
        </div>

        {Boolean(authenticatedUser?.isAdmin) && (
          <div
            onClick={() => navigate('/admin')}
            style={{
              background: '#1a1a2e',
              border: '1px solid #f59e0b',
              padding: '20px',
              borderRadius: '12px',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'border-color 0.2s',
            }}
          >
            <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🛡️</div>
            <div style={{ fontWeight: '600', fontSize: '14px', color: '#f59e0b' }}>Admin Panel</div>
          </div>
        )}
      </div>
    </div>
  );
}
